import {
  useCallback,
  useMemo,
  useRef,
  useState,
  type DragEvent,
  type PointerEvent,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Kanban, GripVertical, Loader2, Info, RefreshCw } from "lucide-react";
import type { AdminTicket } from "@sd/types/admin";
import { toast } from "sonner";
import { useUser } from "@/context/useUser";
import { getUserInfo } from "@/lib/storeManager";
import { getServiceDeskUserId, useAuth } from "@sd/hooks/useAuth";
import {
  isAgentShellRole,
  isExternalSdRole,
  SD_EXTERNAL_AGENT_ROLE,
  SD_EXTERNAL_CUSTOMER_ROLE,
} from "@sd/lib/sdRoles";
import { useServiceDeskUserDisplayMap } from "@sd/hooks/useServiceDeskUserDisplayMap";
import {
  kanbanColumnCount,
  moveTicketInKanbanData,
  parseKanbanApiResponse,
  type KanbanApiData,
} from "@sd/lib/kanbanTicketsFromPayload";
import { enrichAdminTicketsWithUserMap } from "@sd/lib/userDisplayMap";
import { putTicketFormilyStatus } from "@sd/lib/putTicketFormilyStatus";
import { formatTitleCase } from "@sd/lib/ticketStreamUtils";
import {
  formatStatusLabel,
  statusColors,
  normalizeStatus,
  KANBAN_COLUMN_META,
  AGENT_STATUS_CHANGE_VALUES,
} from "@sd/constants/ticketStatuses";
import { ASSIGNED_SELECT_NONE } from "@sd/lib/adminTicketUtils";
import { SearchableMultiFilterSelect } from "@sd/components/common/SearchableMultiFilterSelect";
import { CreatedDateFilterSelect } from "@sd/components/common/CreatedDateFilterSelect";
import {
  emptyCreatedDateFilter,
  getDefaultCreatedDateRange,
  hasCreatedDateFilter,
  type CreatedDateFilterState,
} from "@sd/lib/ticketCreatedDateFilter";
import { projectFilterOptionsWithSelection } from "@sd/lib/ptsDropdownOptions";
import { useKanbanDragAutoScroll } from "@sd/hooks/useKanbanDragAutoScroll";
import { TicketDetailDrawer } from "@sd/components/tickets/TicketDetailDrawer";
import { buildTicketDetailPath } from "@sd/lib/ticketDetailPath";
import {
  ServiceDeskAPI,
  ServiceDeskAPIService,
  buildKanbanViewQueryParams,
  ptsOpenProjectListQueryKey,
  serviceDeskAgentAssignedProjectsQueryKey,
  serviceDeskKanbanViewQueryKey,
  serviceDeskKanbanViewQueryKeyWith,
  serviceDeskUserProjectQueryKey,
} from "@sd/services/APIs";

type Status = AdminTicket["status"];

const COLUMN_META = KANBAN_COLUMN_META;

/** Agent Kanban — same statuses as the Agent status dropdown. */
const AGENT_DRAG_FROM_STATUSES: Status[] = [...AGENT_STATUS_CHANGE_VALUES];
const AGENT_DRAG_TO_STATUSES: Status[] = [...AGENT_STATUS_CHANGE_VALUES];

const AGENT_KANBAN_HELP =
  "As an Agent, you can only drag tickets between In Progress, Fixed in Development, Ready for QA, and Local Updated. All other columns stay locked.";

const EXTERNAL_KANBAN_HELP =
  "You can drag tickets to any status column, including Close.";

function kanbanPermissionCopy(role: string | null | undefined): {
  title: string;
  body: string;
} {
  if (role === SD_EXTERNAL_AGENT_ROLE) {
    return {
      title: "External Agent permission: ",
      body: EXTERNAL_KANBAN_HELP,
    };
  }
  if (role === SD_EXTERNAL_CUSTOMER_ROLE) {
    return {
      title: "External Customer permission: ",
      body: EXTERNAL_KANBAN_HELP,
    };
  }
  return {
    title: "Agent permission: ",
    body: AGENT_KANBAN_HELP,
  };
}

function isAgentKanbanTransitionAllowed(from: Status, to: Status): boolean {
  return (
    AGENT_DRAG_FROM_STATUSES.includes(from) &&
    AGENT_DRAG_TO_STATUSES.includes(to) &&
    from !== to
  );
}
/** Cannot move backward from fixed columns into pending columns. */
const BACKWARD_BLOCKED_TARGETS: Status[] = ["pending_from_client"];
const BACKWARD_BLOCKED_SOURCES: Status[] = [
  "ready_for_qa",
  "fixed_in_development",
];

const priorityColors: Record<string, string> = {
  P1: "bg-destructive text-destructive-foreground",
  P2: "bg-orange-500 text-white",
  P3: "bg-yellow-500 text-white",
  P4: "bg-muted text-muted-foreground",
};

/** User id from `POST /auth/login/` (and JWT / local store) for `kanban-view?emp_id=`.
 *  Priority: login response `id` → JWT id fields.
 */
function kanbanAuthUserId(
  decodedToken: unknown,
  userInfo: unknown
): string | undefined {
  const info = userInfo as { id?: string } | null;
  const fromLogin = String(info?.id ?? "").trim();
  if (fromLogin) return fromLogin;

  const d = decodedToken as {
    id?: string;
    user_id?: string;
    userId?: string;
    sub?: string;
  } | null;
  const raw = String(d?.id ?? d?.user_id ?? d?.userId ?? d?.sub ?? "").trim();
  return raw || undefined;
}

export function AdminKanban({
  empId,
  enableFilters = false,
}: {
  empId?: string;
  /** Show assignee / project / date filters on the kanban toolbar. */
  enableFilters?: boolean;
} = {}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const didDragRef = useRef(false);
  const pointerDownRef = useRef<{ x: number; y: number } | null>(null);
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [previewTicketId, setPreviewTicketId] = useState<string | null>(null);
  const [projectFilters, setProjectFilters] = useState<string[]>([]);
  const [openFilterId, setOpenFilterId] = useState<string | null>(null);
  const [assigneeFilter, setAssigneeFilter] = useState("all");

  const [createdDateFilter, setCreatedDateFilter] =
    useState<CreatedDateFilterState>(getDefaultCreatedDateRange());
  const { userDisplayMap, agentEmployees } = useServiceDeskUserDisplayMap({
    refetchAgentListsOnMount: enableFilters,
  });
  const { role } = useAuth();
  const isAgentOnly = isAgentShellRole(role);
  const kanbanPermission = kanbanPermissionCopy(role);
  const isSuperAgent = role === "ServiceDesk_SuperAgent";
  const isExternalUser = isExternalSdRole(role);
  const isAgentOnlyRole = role === "ServiceDesk_Agent";
  const scopedUserId =
    isExternalUser || isAgentOnlyRole ? getServiceDeskUserId() : null;
  const externalUserId = isExternalUser ? scopedUserId : null;
  const agentEmpId = isAgentOnlyRole ? scopedUserId : null;

  const {
    data: assignedProjectsData,
    isFetched: assignedProjectsFetched,
  } = useQuery({
    queryKey: serviceDeskUserProjectQueryKey(externalUserId),
    queryFn: async () => {
      const response = await ServiceDeskAPIService.userProjects.list(
        externalUserId ?? undefined
      );
      return response.data;
    },
    enabled: enableFilters && Boolean(externalUserId),
    staleTime: 15_000,
  });

  const {
    data: agentAssignedProjectsData,
    isFetched: agentAssignedProjectsFetched,
  } = useQuery({
    queryKey: serviceDeskAgentAssignedProjectsQueryKey(agentEmpId),
    queryFn: async () => {
      const response = await ServiceDeskAPIService.agentAssignedProjects.list(
        agentEmpId ?? undefined
      );
      return response.data;
    },
    enabled: enableFilters && Boolean(agentEmpId),
    staleTime: 15_000,
  });

  const { data: ptsProjectsData } = useQuery({
    queryKey: ptsOpenProjectListQueryKey,
    queryFn: async () => {
      const response = await ServiceDeskAPIService.pts.openProjectList();
      return response.data;
    },
    enabled: enableFilters && !isExternalUser && !isAgentOnlyRole,
    staleTime: 5 * 60 * 1000,
  });

  const projectFilterSelectOptions = useMemo(() => {
    const source = isExternalUser
      ? assignedProjectsData
      : isAgentOnlyRole
        ? agentAssignedProjectsData
        : ptsProjectsData;
    return projectFilterOptionsWithSelection(source, projectFilters);
  }, [
    agentAssignedProjectsData,
    assignedProjectsData,
    isAgentOnlyRole,
    isExternalUser,
    projectFilters,
    ptsProjectsData,
  ]);

  const assignedProjectIds = useMemo(
    () =>
      isExternalUser || isAgentOnlyRole
        ? projectFilterSelectOptions.map(option => option.value)
        : null,
    [isAgentOnlyRole, isExternalUser, projectFilterSelectOptions]
  );

  const effectiveProjectFilters = useMemo(() => {
    if (!enableFilters) return [];
    if (!assignedProjectIds) return projectFilters;
    if (assignedProjectIds.length === 0) return [];
    if (projectFilters.length === 0) return assignedProjectIds;
    const allowed = projectFilters.filter(id => assignedProjectIds.includes(id));
    return allowed.length > 0 ? allowed : assignedProjectIds;
  }, [assignedProjectIds, enableFilters, projectFilters]);
  const effectiveAssigneeFilter =
    enableFilters && !isExternalUser ? assigneeFilter : "all";
  const projectFilterKey =
    effectiveProjectFilters.length > 0
      ? effectiveProjectFilters.join(",")
      : "all";

  const hasKanbanFilters =
    enableFilters &&
    (projectFilters.length > 0 ||
      (!isExternalUser && assigneeFilter !== "all") ||
      hasCreatedDateFilter(createdDateFilter));

  const resetKanbanFilters = () => {
    setProjectFilters([]);
    setAssigneeFilter("all");
    setCreatedDateFilter(emptyCreatedDateFilter());
  };

  const kanbanQueryKey = useMemo(
    () =>
      serviceDeskKanbanViewQueryKeyWith(
        empId,
        projectFilterKey,
        effectiveAssigneeFilter,
        createdDateFilter
      ),
    [empId, projectFilterKey, effectiveAssigneeFilter, createdDateFilter]
  );

  const {
    data: kanbanData,
    isPending,
    isError,
    error,
  } = useQuery({
    queryKey: kanbanQueryKey,
    queryFn: async ({ queryKey }) => {
      if (assignedProjectIds && assignedProjectIds.length === 0) {
        return parseKanbanApiResponse({});
      }
      const empKey = queryKey[2] as string | null;
      const projectKey = String(queryKey[3] ?? "all");
      const assigneeKey = String(queryKey[4] ?? "all");
      const params = buildKanbanViewQueryParams(
        empKey ?? undefined,
        projectKey,
        assigneeKey,
        ASSIGNED_SELECT_NONE,
        createdDateFilter
      );
      const res = await ServiceDeskAPI.kanban.view(
        Object.keys(params).length > 0 ? params : undefined
      );
      if (!res) {
        throw new Error("Failed to load kanban board.");
      }
      return parseKanbanApiResponse(res.data ?? {});
    },
    enabled: isExternalUser
      ? assignedProjectsFetched
      : isAgentOnlyRole && enableFilters
        ? agentAssignedProjectsFetched
        : true,
    retry: 1,
    placeholderData: keepPreviousData,
  });

  const rawTickets = kanbanData?.tickets ?? [];
  const columnCounts = kanbanData?.columnCounts ?? {};
  const boardTotal = kanbanData?.total;

  const tickets = useMemo(
    () => enrichAdminTicketsWithUserMap(rawTickets, userDisplayMap),
    [rawTickets, userDisplayMap]
  );

  const { scrollRef } = useKanbanDragAutoScroll(draggedId !== null);
  const kanbanReturnTo = `${location.pathname}${location.search}`;
  const previewTicket = tickets.find(t => t.id === previewTicketId);

  const endDrag = useCallback(() => {
    setDraggedId(null);
    window.setTimeout(() => {
      didDragRef.current = false;
    }, 0);
  }, []);

  const openTicketDrawer = (ticketId: string) => {
    if (didDragRef.current) return;
    setPreviewTicketId(ticketId);
  };

  const markCardPointerDown = (e: PointerEvent) => {
    if (e.button !== 0) return;
    pointerDownRef.current = { x: e.clientX, y: e.clientY };
    didDragRef.current = false;
  };

  const markCardDragMove = (e: DragEvent) => {
    if (e.clientX === 0 && e.clientY === 0) return;
    const start = pointerDownRef.current;
    if (!start) return;
    if (
      Math.abs(e.clientX - start.x) > 8 ||
      Math.abs(e.clientY - start.y) > 8
    ) {
      didDragRef.current = true;
    }
  };

  const openTicketDetailPage = (ticketId: string) => {
    navigate(buildTicketDetailPath(ticketId, { returnTo: kanbanReturnTo }));
  };

  const handleDrop = useCallback(
    async (e: DragEvent, status: Status) => {
      e.preventDefault();
      if (!draggedId) return;
      const ticket = tickets.find(t => t.id === draggedId);
      endDrag();
      if (!ticket) return;
      const fromStatus = normalizeStatus(ticket.status);
      if (fromStatus === status) return;

      /* External Agent / Customer: all status columns, including Close. */
      if (!isExternalUser) {
        /* Global restriction: fixed → pending columns is a backward move */
        if (
          BACKWARD_BLOCKED_SOURCES.includes(fromStatus) &&
          BACKWARD_BLOCKED_TARGETS.includes(status)
        ) {
          toast.error(
            `${formatStatusLabel(fromStatus)} tickets cannot be moved back to ${formatStatusLabel(status)}`
          );
          return;
        }

        /* Close tickets: Super Agent may move them; Agent / others cannot. */
        if (fromStatus === "close" && !isSuperAgent) {
          toast.error("Closed tickets cannot be moved");
          return;
        }

        /* Agent restriction: workflow moves involving Fixed / Fixed in Development */
        if (isAgentOnly) {
          if (!isAgentKanbanTransitionAllowed(fromStatus, status)) {
            toast.error(kanbanPermission.body);
            return;
          }
        }
      }

      const previousData =
        queryClient.getQueryData<KanbanApiData>(kanbanQueryKey);
      queryClient.setQueryData<KanbanApiData>(kanbanQueryKey, current =>
        current ? moveTicketInKanbanData(current, ticket.id, status) : current
      );

      const ok = await putTicketFormilyStatus(queryClient, ticket.id, status);
      if (!ok) {
        queryClient.setQueryData(kanbanQueryKey, previousData);
        void queryClient.invalidateQueries({
          queryKey: serviceDeskKanbanViewQueryKey,
        });
        return;
      }
      toast.success(
        `${ticket.uiId ?? ticket.id} → ${formatStatusLabel(status)}`
      );
    },
    [
      draggedId,
      queryClient,
      tickets,
      isAgentOnly,
      isExternalUser,
      isSuperAgent,
      kanbanPermission.body,
      endDrag,
      kanbanQueryKey,
    ]
  );

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col gap-4 p-4 sm:gap-6 sm:p-6">
      <div className="min-w-0 shrink-0">
        <h1 className="flex items-center gap-2 text-2xl font-bold text-foreground sm:text-3xl">
          <Kanban className="h-6 w-6 shrink-0 text-primary sm:h-8 sm:w-8" />{" "}
          Kanban Board
        </h1>
        <p className="mt-1 text-sm text-muted-foreground sm:text-base">
          Drag tickets between columns to update status. Hold a card near the
          left or right edge to scroll across columns.
        </p>
      </div>

      <div className="flex shrink-0 flex-wrap items-center gap-2 rounded-lg border border-border bg-card p-3">
        {enableFilters ? (
          <>
            {isExternalUser ? null : (
            <Select value={assigneeFilter} onValueChange={setAssigneeFilter}>
              <SelectTrigger className="h-9 w-full shrink-0 rounded-lg text-sm sm:w-[170px]">
                <SelectValue placeholder="All Assignees" />
              </SelectTrigger>
              <SelectContent className="max-h-64">
                <SelectItem value="all">All Assignees</SelectItem>
                <SelectItem value={ASSIGNED_SELECT_NONE}>Unassigned</SelectItem>
                {agentEmployees.map(agent => (
                  <SelectItem
                    key={`kanban-filter-${agent.id}`}
                    value={agent.id}
                  >
                    {agent.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            )}
            <SearchableMultiFilterSelect
              selectId="kanban-project"
              openSelectId={openFilterId}
              onOpenSelectIdChange={setOpenFilterId}
              values={projectFilters}
              onValuesChange={setProjectFilters}
              options={projectFilterSelectOptions}
              placeholder="All Projects"
              searchPlaceholder="Search projects…"
              triggerClassName="w-full shrink-0 sm:w-[200px]"
            />
            <CreatedDateFilterSelect
              value={createdDateFilter}
              onChange={setCreatedDateFilter}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-9 shrink-0 gap-1.5"
              onClick={resetKanbanFilters}
              disabled={!hasKanbanFilters}
              title="Clear all filters"
              aria-label="Clear all filters"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Clear All Filters
            </Button>
          </>
        ) : null}
        <Badge
          className={`shrink-0 rounded-full border-0 bg-primary/10 px-3 py-1 text-xs font-semibold text-primary ${enableFilters ? "ml-auto" : ""}`}
        >
          {isPending
            ? "Loading…"
            : boardTotal !== undefined
              ? `${boardTotal} tickets`
              : `${tickets.length} tickets`}
        </Badge>
      </div>

      {/* Agent / External Agent / External Customer restriction banner */}
      {isAgentOnly && (
        <div className="flex shrink-0 items-start gap-3 rounded-lg border border-amber-300/60 bg-amber-50/80 px-3 py-3 dark:border-amber-700/50 dark:bg-amber-950/30 sm:px-4">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
          <p className="min-w-0 whitespace-normal text-sm leading-5 text-amber-800 dark:text-amber-200">
            <span className="font-semibold">{kanbanPermission.title}</span>
            {kanbanPermission.body}
          </p>
        </div>
      )}

      {isError && (
        <p className="text-sm text-destructive" role="alert">
          {error instanceof Error
            ? error.message
            : "Failed to load kanban board."}
        </p>
      )}

      {isPending ? (
        <div className="flex items-center justify-center gap-2 py-24 text-muted-foreground">
          <Loader2 className="h-6 w-6 animate-spin" />
          <span>Loading board…</span>
        </div>
      ) : !isError ? (
        <div className="flex h-[calc(100dvh-13rem)] min-h-[420px] flex-1 flex-col">
          <div
            ref={scrollRef}
            className={`min-h-0 flex-1 overflow-x-auto overflow-y-hidden overscroll-x-contain ${
              draggedId ? "cursor-grabbing" : ""
            }`}
          >
            <div className="flex h-full min-w-max gap-3 px-0.5 pb-1">
              {COLUMN_META.map(col => {
                const colTickets = tickets.filter(
                  t => normalizeStatus(t.status) === col.id
                );
                const isDropAllowed =
                  isExternalUser ||
                  !isAgentOnly ||
                  AGENT_DRAG_TO_STATUSES.includes(col.id);
                const headerAccent =
                  statusColors[col.id]
                    ?.split(" ")
                    .find(c => c.startsWith("bg-")) ?? "bg-muted";
                return (
                  <div
                    key={col.id}
                    onDragOver={
                      isDropAllowed ? e => e.preventDefault() : undefined
                    }
                    onDrop={
                      isDropAllowed
                        ? e => void handleDrop(e, col.id)
                        : undefined
                    }
                    className={`flex h-full w-[min(240px,calc(100vw-2.5rem))] shrink-0 flex-col overflow-hidden rounded-lg border-2 sm:w-[240px] ${col.color} ${
                      !isDropAllowed ? "opacity-90" : ""
                    }`}
                  >
                    <div
                      className="z-10 shrink-0 border-b border-border/60 bg-card/95 px-3 py-2.5 shadow-sm backdrop-blur-sm"
                      title={col.label}
                    >
                      <div className="flex items-start gap-2">
                        <span
                          className={`mt-1 h-8 w-1 shrink-0 rounded-full ${headerAccent}`}
                          aria-hidden
                        />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-start justify-between gap-2">
                            <h3 className="line-clamp-2 text-sm font-semibold leading-snug text-foreground">
                              {col.label}
                            </h3>
                            <Badge
                              variant="secondary"
                              className="shrink-0 tabular-nums"
                            >
                              {kanbanColumnCount(col.id, columnCounts, tickets)}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="min-h-0 flex-1 space-y-2 overflow-y-auto overscroll-y-contain p-2">
                      {colTickets.map(t => {
                        /*
                      Drag rules:
                        - Agent: closed never draggable; only allowed source statuses
                        - Super Agent: all statuses including Close
                        - Admin: closed locked, all others draggable
                    */
                        const ticketStatus = normalizeStatus(t.status);
                        const isDraggable = isExternalUser
                          ? true
                          : isAgentOnly
                            ? ticketStatus !== "close" &&
                              AGENT_DRAG_FROM_STATUSES.includes(ticketStatus)
                            : isSuperAgent
                              ? true
                              : ticketStatus !== "close";

                        return (
                          <Card
                            key={t.id}
                            draggable={isDraggable}
                            onPointerDown={markCardPointerDown}
                            onDragStart={
                              isDraggable ? () => setDraggedId(t.id) : undefined
                            }
                            onDrag={isDraggable ? markCardDragMove : undefined}
                            onDragEnd={isDraggable ? endDrag : undefined}
                            onClick={() => openTicketDrawer(t.id)}
                            className={`cursor-pointer transition hover:shadow-md ${
                              isDraggable
                                ? "active:cursor-grabbing"
                                : "opacity-90"
                            } ${
                              draggedId === t.id
                                ? "opacity-60 ring-2 ring-primary/40"
                                : ""
                            }`}
                          >
                            <CardContent className="p-3">
                              <div className="flex items-start gap-2">
                                <GripVertical
                                  className={`mt-0.5 h-4 w-4 flex-shrink-0 text-muted-foreground ${
                                    isDraggable ? "cursor-grab" : ""
                                  }`}
                                  onClick={e => e.stopPropagation()}
                                />
                                <div className="min-w-0 flex-1">
                                  <div className="mb-1 flex items-center gap-2">
                                    <Badge
                                      className={
                                        priorityColors[t.priority] ?? ""
                                      }
                                      variant="secondary"
                                    >
                                      {t.priority}
                                    </Badge>
                                    <button
                                      type="button"
                                      className="shrink-0 whitespace-nowrap font-mono text-xs text-muted-foreground hover:text-primary hover:underline"
                                      title="Open ticket detail page"
                                      onClick={e => {
                                        e.stopPropagation();
                                        openTicketDetailPage(t.id);
                                      }}
                                    >
                                      {t.uiId ?? t.id}
                                    </button>
                                  </div>
                                  <p
                                    className="mb-1 line-clamp-4 break-normal text-sm font-medium leading-snug [overflow-wrap:break-word] [word-break:normal]"
                                    title={t.subject}
                                  >
                                    {t.subject}
                                  </p>
                                  {isExternalUser ? null : (
                                  <p className="truncate text-xs text-muted-foreground">
                                    {t.requester}
                                  </p>
                                  )}
                                  <div className="mt-2 flex items-center justify-between">
                                    <Badge
                                      variant="outline"
                                      className="text-xs"
                                    >
                                      {t.category
                                        ? formatTitleCase(t.category)
                                        : "—"}
                                    </Badge>
                                    {t.assignedAgent ? (
                                      <span className="max-w-[80px] truncate text-xs text-muted-foreground">
                                        {t.assignedAgent.split(" ")[0]}
                                      </span>
                                    ) : null}
                                  </div>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                      {colTickets.length === 0 && (
                        <div className="py-8 text-center text-xs text-muted-foreground">
                          {isDropAllowed ? "Drop tickets here" : "No tickets"}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ) : null}

      <TicketDetailDrawer
        ticketId={previewTicketId}
        fallbackLabel={previewTicket?.uiId ?? previewTicket?.id}
        returnTo={kanbanReturnTo}
        onClose={() => setPreviewTicketId(null)}
      />
    </div>
  );
}
